// @ts-nocheck
import { useState, useEffect, memo } from 'react'
import { Pause, Play, Clock, ChevronDown, ChevronUp } from 'lucide-react'
import { formatPhoneNumberForDisplay } from '../utils/phoneNumberHelper'

/**
 * 保留中通話インジケーター
 * 画面左下に固定表示され、手動で閉じることはできない
 * React.memoで最適化: calls配列とonResume関数が変更された時のみ再レンダリング
 *
 * @param {Array} calls - 保留中の通話一覧
 * @param {Function} onResume - 保留解除時のコールバック関数
 */
const HoldIndicator = memo(({ calls, onResume }) => {
  const [resuming, setResuming] = useState(null)
  const [minimized, setMinimized] = useState(false)
  const [updateTrigger, setUpdateTrigger] = useState(Date.now())

  // 保留時間を1秒ごとに更新（表示のみ）
  useEffect(() => {
    if (calls.length === 0) return
    
    const timer = setInterval(() => {
      setUpdateTrigger(Date.now())
    }, 1000)
    
    return () => clearInterval(timer)
  }, [calls.length])

  // 保留中の通話がない場合は何も表示しない
  if (calls.length === 0) {
    return null
  }

  /**
   * 保留解除を実行
   */
  const handleResume = async (holdQueueName) => {
    if (resuming) return // 二重実行防止
    
    setResuming(holdQueueName)
    try {
      await onResume(holdQueueName)
      // 成功したらresumingをクリアせず、着信を待つ
      // 着信が来た時点でリストから削除される
    } catch (error) {
      console.error('保留解除エラー:', error)
      setResuming(null) // エラー時のみクリア
      
      // エラーメッセージを表示
      const errorMessage = error.message || '保留解除に失敗しました'
      
      // 409エラー（他のオペレータが既に対応）の場合は特別なメッセージ
      if (errorMessage.includes('他のオペレータ')) {
        alert('この通話は既に他のオペレータが対応しています。\n発信者が通話を終了したか、他の担当者が応答した可能性があります。')
      } else {
        alert(`保留解除に失敗しました: ${errorMessage}`)
      }
    }
  }

  /**
   * 保留時間をフォーマット
   */
  const formatDuration = (heldAt) => {
    const seconds = Math.floor((Date.now() - heldAt) / 1000)
    const minutes = Math.floor(seconds / 60)
    const hours = Math.floor(minutes / 60)
    
    if (hours > 0) {
      return `${hours}時間${minutes % 60}分`
    }
    if (minutes > 0) {
      return `${minutes}分${seconds % 60}秒`
    }
    return `${seconds}秒`
  }

  /**
   * 保留時間が5分以上かチェック
   */
  const isLongHold = (heldAt) => {
    return Date.now() - heldAt > 5 * 60 * 1000
  }

  return (
    <div className="fixed bottom-4 left-4 z-50 max-w-sm animate-in slide-in-from-bottom-4 duration-300">
      {/* ヘッダー */}
      <div className="bg-yellow-500 dark:bg-yellow-600 text-white rounded-t-lg px-4 py-2 flex items-center justify-between shadow-lg">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
            <Pause className="w-5 h-5" />
          </div>
          <span className="font-semibold text-sm">
            保留中の通話 ({calls.length})
          </span>
        </div>
        <button
          onClick={() => setMinimized(!minimized)}
          className="hover:bg-white hover:bg-opacity-20 rounded p-1.5 transition-colors"
          title={minimized ? '展開' : '最小化'}
        >
          {minimized ? (
            <ChevronUp className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </button>
      </div>

      {/* 保留中の通話リスト */}
      {!minimized && (
        <div className="bg-white dark:bg-gray-800 rounded-b-lg shadow-lg overflow-hidden">
          <div className="max-h-96 overflow-y-auto">
            {calls.map((call, index) => {
              const isLong = isLongHold(call.heldAt)
              const isResuming = call.status === 'resuming' || resuming === call.holdQueueName
              
              return (
                <div
                  key={call.holdQueueName}
                  className={`p-4 ${
                    index !== calls.length - 1
                      ? 'border-b border-gray-200 dark:border-gray-700'
                      : ''
                  } ${
                    isResuming
                      ? 'bg-blue-50 dark:bg-blue-900/10'
                      : isLong
                      ? 'bg-red-50 dark:bg-red-900/10'
                      : ''
                  }`}
                >
                  {/* 発信者情報 */}
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      {call.customerName ? (
                        <>
                          <div className="text-base font-semibold text-gray-900 dark:text-white truncate">
                            {call.customerName}
                          </div>
                          <div className="text-xs text-gray-600 dark:text-gray-400">
                            {formatPhoneNumberForDisplay(call.callerNumber)}
                          </div>
                        </>
                      ) : (
                        <div className="text-base font-semibold text-gray-900 dark:text-white truncate">
                          {formatPhoneNumberForDisplay(call.callerNumber)}
                        </div>
                      )}
                      <div className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                        保留者: {call.operatorName}
                      </div>
                    </div>
                  </div>

                  {/* 保留時間 */}
                  <div className={`flex items-center gap-2 text-xs mb-3 ${
                    isLong
                      ? 'text-red-700 dark:text-red-300 font-semibold'
                      : 'text-gray-500 dark:text-gray-400'
                  }`}>
                    <Clock className="w-3.5 h-3.5" />
                    <span>保留時間: {formatDuration(call.heldAt)}</span>
                  </div>

                  {/* 再開中の表示 */}
                  {isResuming && (
                    <div className="mb-3 bg-blue-100 dark:bg-blue-900/30 border border-blue-300 dark:border-blue-800 rounded-md p-2">
                      <p className="text-xs text-blue-800 dark:text-blue-200 font-medium flex items-center gap-1">
                        <span className="animate-pulse">🔄</span>
                        通話を再開中です...
                      </p>
                    </div>
                  )}

                  {/* 警告表示（5分以上保留） */}
                  {isLong && !isResuming && (
                    <div className="mb-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-800 rounded-md p-2">
                      <p className="text-xs text-red-800 dark:text-red-200 font-medium flex items-center gap-1">
                        <span className="text-base">⚠️</span>
                        保留時間が長くなっています
                      </p>
                    </div>
                  )}

                  {/* 再開ボタン */}
                  <button
                    onClick={() => handleResume(call.holdQueueName)}
                    disabled={isResuming}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold text-sm transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed shadow-sm"
                  >
                    <Play className="w-4 h-4" />
                    {isResuming ? '再開中...' : '通話を再開'}
                  </button>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
})

export default HoldIndicator
